
import { useState, useEffect, useRef } from 'react';

declare global {
  interface Window {
    BMap: any;
    BMapGL: any;
    initBaiduMap: () => void;
  }
}

const CITIES = {
  foshan: {
    name: '佛山',
    position: { lng: 113.134, lat: 23.035 },
    categories: ['家居五金', '铝材天花', '板材饰面'],
    advantages: '珠三角核心制造基地，五金与铝材产业集群',
    responseTime: '< 2小时',
    dataComplete: '92%',
    dataCompleteNum: 92,
    countries: '85+',
    countriesNum: 85,
  },
  zhaoqing: {
    name: '肇庆',
    position: { lng: 112.484, lat: 23.057 },
    categories: ['涂料胶水', '木业夹板'],
    advantages: '涂料与木业加工中心，环保认证齐全',
    responseTime: '< 3小时',
    dataComplete: '88%',
    dataCompleteNum: 88,
    countries: '72+',
    countriesNum: 72,
  },
  shanwei: {
    name: '汕尾',
    position: { lng: 115.375, lat: 22.787 },
    categories: ['家居五金', '板材饰面'],
    advantages: '粤东沿海制造基地，出口物流便利',
    responseTime: '< 4小时',
    dataComplete: '85%',
    dataCompleteNum: 85,
    countries: '68+',
    countriesNum: 68,
  },
  jieyang: {
    name: '揭阳',
    position: { lng: 116.379, lat: 23.549 },
    categories: ['木业夹板', '涂料胶水'],
    advantages: '木材加工产业带，定制化能力强',
    responseTime: '< 3小时',
    dataComplete: '86%',
    dataCompleteNum: 86,
    countries: '70+',
    countriesNum: 70,
  },
};

const BUSINESS_FILTERS = [
  { id: 'stock', label: '现货', active: true },
  { id: 'mixed', label: '混批', active: false },
  { id: 'custom', label: '可定制', active: false },
  { id: 'complete', label: '资料齐全', active: false },
];

export default function BeltMap() {
  const [selectedCity, setSelectedCity] = useState<keyof typeof CITIES>('foshan');
  const [filters, setFilters] = useState(BUSINESS_FILTERS);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<Record<string, any>>({});

  const currentCity = CITIES[selectedCity];

  const toggleFilter = (id: string) => {
    setFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, active: !f.active } : f))
    );
  };

  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current || !window.BMap) return;

      const BMap = window.BMap;
      const map = new BMap.Map(mapRef.current);
      mapInstanceRef.current = map;

      const center = new BMap.Point(113.8, 23.2);
      map.centerAndZoom(center, 8);
      map.enableScrollWheelZoom(true);
      map.addControl(new BMap.NavigationControl());
      map.addControl(new BMap.ScaleControl());

      Object.entries(CITIES).forEach(([key, city]) => {
        const point = new BMap.Point(city.position.lng, city.position.lat);
        const markerIcon = new BMap.Icon(
          'https://api.map.baidu.com/images/marker_red.png',
          new BMap.Size(23, 25)
        );
        const marker = new BMap.Marker(point, { icon: markerIcon });
        map.addOverlay(marker);
        markersRef.current[key] = marker;

        const label = new BMap.Label(city.name, {
          offset: new BMap.Size(-16, -40),
        });
        label.setStyle({
          color: '#8B1A1A',
          fontSize: '13px',
          fontWeight: 'bold',
          border: '1px solid #8B1A1A',
          borderRadius: '6px',
          padding: '2px 8px',
          background: 'rgba(255,255,255,0.95)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        });
        marker.setLabel(label);

        const clickHandler = () => setSelectedCity(key as keyof typeof CITIES);
        marker.addEventListener('click', clickHandler);
        label.addEventListener('click', clickHandler);
      });
    };

    if (window.BMap) {
      initMap();
      return;
    }

    window.initBaiduMap = initMap;

    if (!document.getElementById('baidu-map-script')) {
      const script = document.createElement('script');
      script.id = 'baidu-map-script';
      script.src =
        'https://api.map.baidu.com/api?v=3.0&ak=OkgxfckQiFXynCadbYoo9YxkcvRt87XN&callback=initBaiduMap';
      script.charset = 'utf-8';
      script.async = true;
      document.head.appendChild(script);
    }

    return () => {
      window.initBaiduMap = () => {};
      const script = document.getElementById('baidu-map-script');
      if (script) script.remove();
    };
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current || !window.BMap) return;
    const BMap = window.BMap;
    Object.entries(markersRef.current).forEach(([key, marker]) => {
      const label = marker.getLabel();
      if (key === selectedCity) {
        label?.setStyle({
          color: '#fff',
          fontSize: '13px',
          fontWeight: 'bold',
          border: '1px solid #8B1A1A',
          borderRadius: '6px',
          padding: '2px 8px',
          background: '#8B1A1A',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        });
        const city = CITIES[key as keyof typeof CITIES];
        mapInstanceRef.current.panTo(
          new BMap.Point(city.position.lng, city.position.lat)
        );
      } else {
        label?.setStyle({
          color: '#8B1A1A',
          fontSize: '13px',
          fontWeight: 'bold',
          border: '1px solid #8B1A1A',
          borderRadius: '6px',
          padding: '2px 8px',
          background: 'rgba(255,255,255,0.95)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        });
      }
    });
  }, [selectedCity]);

  return (
    <section className="py-24 bg-gradient-to-b from-white to-[#fdf6f0]">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">产业带联动地图</h2>
          <p className="text-lg text-gray-500 max-w-[560px] mx-auto">
            四城协同，覆盖建材家居全品类供应链
          </p>
        </div>

        <div className="grid grid-cols-3 gap-8">
          {/* 左侧地图 */}
          <div className="col-span-2">
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100 hover:shadow-xl transition-all">
              <div ref={mapRef} className="w-full" style={{ height: '520px' }} />

              {/* 业务筛选 */}
              <div className="p-6 bg-white border-t border-gray-100">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm font-medium text-gray-500">业务筛选：</span>
                  <div className="flex gap-3 flex-wrap">
                    {filters.map((filter) => (
                      <button
                        key={filter.id}
                        onClick={() => toggleFilter(filter.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                          filter.active
                            ? 'bg-[#8B1A1A] text-white shadow-md'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 右侧城市面板 */}
          <div className="col-span-1">
            <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100 hover:shadow-xl transition-all sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">{currentCity.name}</h3>
                <div className="w-10 h-10 flex items-center justify-center bg-[#8B1A1A]/10 rounded-lg">
                  <i className="ri-map-pin-fill text-xl text-[#8B1A1A]"></i>
                </div>
              </div>

              {/* 微指标条 */}
              <div className="space-y-4 mb-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">响应时效</span>
                    <span className="text-sm font-bold text-[#8B1A1A]">{currentCity.responseTime}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#8B1A1A] to-[#C9A84C]" style={{ width: '95%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">资料齐全度</span>
                    <span className="text-sm font-bold text-[#C9A84C]">{currentCity.dataComplete}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#C9A84C]" style={{ width: `${currentCity.dataCompleteNum}%` }} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">覆盖国家</span>
                    <span className="text-sm font-bold text-[#7A3B1E]">{currentCity.countries}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#7A3B1E] to-[#C9A84C]"
                      style={{ width: `${Math.round((currentCity.countriesNum / 100) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* 城市优势 */}
              <div className="mb-5">
                <h4 className="text-sm font-bold text-gray-900 mb-2">产业优势</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{currentCity.advantages}</p>
              </div>

              {/* 优势品类 */}
              <div className="mb-6">
                <h4 className="text-sm font-bold text-gray-900 mb-3">优势品类</h4>
                <div className="flex flex-wrap gap-2">
                  {currentCity.categories.map((cat, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 bg-[#8B1A1A]/8 text-[#8B1A1A] rounded-full text-xs font-medium border border-[#8B1A1A]/20 whitespace-nowrap"
                    >
                      {cat}
                    </span>
                  ))}
                </div>
              </div>

              {/* 一键生成RFQ */}
              <button className="w-full px-6 py-3 bg-gradient-to-r from-[#8B1A1A] to-[#C9A84C] text-white rounded-xl font-bold hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-file-add-line mr-2"></i>
                一键生成 RFQ
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
